import {
	calculateFocusableElements,
	getFocusableInside,
	handleVisibleChange,
	instances,
} from './dropdown';
import initialize from './utilities/initialize';

const _primaryNav = document.querySelector( '[data-dropdown="primary-nav"]' );
const _primarySearch = document.querySelector( '[data-dropdown="primary-search"]' );
const _searchContent = _primaryNav.querySelector( '.primary-search' );
const _hiddenLinks = Array.from( _primaryNav.querySelectorAll( '.menu-item[data-dropdown] > a' ) );

// Get all primary nav items with children.
const _subNavMenus = _primaryNav.querySelectorAll(
	'.menu-item-has-children'
);

// Get all second level menu items.
const _secondLevelMenus = _primaryNav.querySelectorAll(
	'.primary-nav > .menu-item-has-children'
);

// Get all third level menu items.
const _thirdLevelMenus = _primaryNav.querySelectorAll(
	'.menu-item-has-children .menu-item-has-children'
);

/**
 * @returns {IntersectionObserver} A configured observer, ready to observe
 */
function createObserver() {
	return new IntersectionObserver( handleIntersection );
}

/**
 * Manipulate primary nav to have the viewport-correct behavior.
 *
 * The primary nav behaves differently on "mobile" and "desktop"--it uses
 * the dropdown hide/show functionality on mobile, but not on desktop. Here,
 * we change change the appropriate states so that the menu behaves correctly,
 * using the visibility of the toggle button to tell us which viewport we're
 * on. By using IntersectionObserver, we don't have to fire the logic once
 * on load and again when the viewport size changes--all of that is handled for
 * us by the browser.
 *
 * @param {IntersectionObserverEntry} entry A thing that was observed intersecting
 */
function processEntry( entry ) {
	const { isIntersecting, target } = entry;

	if ( target === _primaryNav.dropdown.toggle ) {
		if ( ! isIntersecting ) {
			// We're on the desktop
			_primaryNav.dataset.visible = 'yes';
			_primaryNav.dataset.toggleable = 'no';
			_primaryNav.dataset.backdrop = 'inactive';
			_primaryNav.dataset.trap = 'inactive';

			// Third level menus are not toggleable on desktop.
			_thirdLevelMenus.forEach( _thirdLevelMenu => {
				_thirdLevelMenu.dataset.toggleable = 'no';
			} );
		} else {
			// We're on mobile
			_primaryNav.dataset.visible = 'no';
			_primaryNav.dataset.toggleable = 'yes';

			// Third level menus are toggleable on mobile.
			_thirdLevelMenus.forEach( _thirdLevelMenu => {
				_thirdLevelMenu.dataset.toggleable = 'yes';
			} );
		}
	}
}

/**
 * @param {IntersectionObserverEntry[]} entries Things that have been observed intersecting
 */
function handleIntersection( entries ) {
	entries.forEach( processEntry );
}

/**
 * Handles the mutation action of the dropdown.
 *
 * This expands on the base behavior of the dropdown's visibility handler.
 * It applies a class to disable body scrolling while the menu is open, and
 * handles resetting subnavs when the primary nav closes.
 *
 * @param {HTMLElement} dropdown A dropdown wrapper
 */
function handlePrimaryNavVisibleChange( dropdown ) {
	handleVisibleChange( dropdown );
	const menuIsVisible = dropdown.dataset.visible === 'yes';
	const toggleIsVisible = dropdown.dropdown.toggle.offsetParent !== null;
	const headerLogo = _primaryNav.querySelector( '.site-header-branding__logo' );
	const logoIsVisible = headerLogo && headerLogo.offsetParent ? true : false;
	const primarySkip = [
		...( _searchContent ? getFocusableInside( _searchContent ) : [] ),
		...( _hiddenLinks ? _hiddenLinks : [] ),
	];
	if ( ! logoIsVisible ) {
		primarySkip.push( headerLogo );
	}

	/**
	 * Get focusable elements for the primary navigation.
	 *
	 * @returns {{last, allowed, skip, first}} The necessary focusable collections
	 */
	_primaryNav.dropdown.getFocusable = () => {
		return calculateFocusableElements(
			getFocusableInside( _primaryNav ),
			primarySkip
		);
	};

	const hasOpenMobileMenu = menuIsVisible && toggleIsVisible;
	const hasClosedMobileMenu = toggleIsVisible && ! menuIsVisible;
	if ( hasOpenMobileMenu ) {
		// Disable body scrolling.
		document.body.classList.add( 'disable-body-scrolling' );
	} else if ( hasClosedMobileMenu  ) {
		// Allow body scrolling.
		document.body.classList.remove( 'disable-body-scrolling' );

		// Primary nav and subnav interactions.
		_subNavMenus.forEach( _subNavMenu => {
			// Make sure open subnavs aren't triggering the backdrop or staying open.
			_subNavMenu.dataset.backdrop = 'inactive';
			_subNavMenu.dataset.visible = 'no';
		} );
	} else {
		// The menu is untoggleable - we're on desktop.
		// Allow body scrolling.
		document.body.classList.remove( 'disable-body-scrolling' );

		// Make sure open second level menus aren't staying open after escaping the top level.
		_secondLevelMenus.forEach( _secondLevelMenu => {
			_secondLevelMenu.dataset.visible = 'no';
		} );
	}
}

/**
 * Handles the mutation action of the dropdown.
 *
 * This expands on the base behavior of the dropdown's visibility handler.
 * It handles custom visibility changes for subnavs.
 *
 * @param {HTMLElement} dropdown A dropdown wrapper
 */
function handleSubNavVisibleChange( dropdown ) {
	handleVisibleChange( dropdown );
	const isMobile = _primaryNav.dataset.toggleable === 'yes';
	const focusable = isMobile ? getFocusableInside( dropdown ) : getFocusableInside( _primaryNav );
	const skipElements = isMobile ? _hiddenLinks : [];

	// Set the items available in the keyboard trap.
	dropdown.dropdown.getFocusable = () => {
		return calculateFocusableElements(
			focusable,
			skipElements
		);
	};

	if ( isMobile ) {
		return;
	}

	// Disable body scrolling when second level menu is open on desktop.
	// Force the top level trap to be on, so we can escape from any point in the menu.
	const activeDropdowns = instances
		.filter( instance => instance.dataset.backdrop === 'active' );

	if ( activeDropdowns.length > 0 ) {
		document.body.classList.add( 'disable-body-scrolling' );
		_primaryNav.dataset.trap = 'active';
	} else {
		document.body.classList.remove( 'disable-body-scrolling' );
		_primaryNav.dataset.trap = 'inactive';
	}

	// Turn off the neighboring subnavs.
	_secondLevelMenus.forEach( _secondLevelMenu => {
		if (
			dropdown.dataset.visible === 'yes' &&
			_secondLevelMenu !== dropdown &&
			_secondLevelMenu.dataset.visible === 'yes'
		) {
			_secondLevelMenu.dataset.visible = 'no';
		}
	} );
}

/**
 * Handles the mutation action of the dropdown.
 *
 * This expands on the base behavior of the dropdown's visibility handler.
 * It handles custom visibility changes for the primary nav search.
 *
 * @param {HTMLElement} dropdown A dropdown wrapper
 */
function handlePrimarySearchVisibleChange( dropdown ) {
	handleVisibleChange( dropdown );
	const searchVisibility = dropdown.dataset.visible;
	const searchInput = dropdown.querySelector( '#primary-search' );

	/**
	 * Get focusable elements for the submenus.
	 *
	 * @returns {{last, allowed, skip, first}} The necessary focusable collections
	 */
	_primarySearch.dropdown.getFocusable = () => {
		return calculateFocusableElements(
			getFocusableInside( _searchContent ),
			[]
		);
	};

	// Handle focus shift.
	if ( searchVisibility === 'yes' ) {
		searchInput.focus();
	} else if ( searchVisibility === 'no' ) {
		// This needs to be 'no' - it's 'false' on load, and we don't want to focus on page load.
		dropdown.dropdown.toggle.focus();
	}
}

/**
 * Set up the site header.
 */
function initializeSiteHeader() {
	if ( ! _primaryNav ) {
		return;
	}

	// Ensure correct state on load
	handlePrimaryNavVisibleChange( _primaryNav );
	_primaryNav.dropdown.handlers.visibleChange = handlePrimaryNavVisibleChange;

	// Observe the primary nav for desktop/mobile toggling.
	_primaryNav.observer = createObserver();
	_primaryNav.observer.observe( _primaryNav.dropdown.toggle );

	// Set up submenus.
	_subNavMenus.forEach( _subNavMenu => {
		// Ensure dropdown.dropdown exists before proceeding.
		if ( ! _subNavMenu || ! _subNavMenu.dropdown ) {
			return;
		}
		handleSubNavVisibleChange( _subNavMenu );
		_subNavMenu.dropdown.handlers.visibleChange = handleSubNavVisibleChange;
	} );

	if ( ! _primarySearch ) {
		return;
	}

	// Set up primary site search.
	handlePrimarySearchVisibleChange( _primarySearch );
	_primarySearch.dropdown.handlers.visibleChange = handlePrimarySearchVisibleChange;
}

/**
 * Tear down the site header.
 */
function teardownSiteHeader() {
	if ( _primaryNav && _primaryNav.observer ) {
		_primaryNav.observer.disconnect();
	}
	_primaryNav.dropdown.handlers.visibleChange = handleVisibleChange;
}

/**
 * Set up functionality for this module.
 */
function setup() {
	initializeSiteHeader();
}

/**
 * Tear down functionality and side-effects of this module.
 */
function teardown() {
	teardownSiteHeader();
}

export default initialize( setup, teardown );
export {
	setup, teardown,
};
